import React, { useRef, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { orderBy } from '@progress/kendo-data-query';
import { mutate as globalMutate } from 'swr';
import { formRiskReviewData } from './risk-reviews-grid.helper';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import ErrorMessage from '@components-clean/core/error';
import { GridHeaderTitle } from '@components-clean/core/grid/grid.interface';
import Grid from '@components-clean/core/grid-fe';
import { RiskReview } from '@interfaces/report-templates.interface';
import DeleteRiskReviewDialog from '@pages-clean/risk-assessment/monitoring-toolkit/manage-reviews-tab/dialogs/delete-risk-review-dialog';
import ManageRiskReviewDialog from '@pages-clean/risk-assessment/monitoring-toolkit/manage-reviews-tab/manage-risk-review-dialog';
import { getRiskReviewsColumns } from '@pages-clean/risk-assessment/monitoring-toolkit/manage-reviews-tab/risk-reviews-grid/risk-reviews-grid.data';
import { PersistentStorageKeys } from '@utils-clean/persistent-storage-keys';
import { AuditApplicationArea, AuditDataType, AuditServiceType } from 'types/graphql-global-types';
import { NullableStudy } from '@contexts/local-state-context';
import { Permissions } from '@contexts/user-context';
import { useAuthorization, useBoolean, useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import { stringDateWithoutTimeFromCurrentUtcDate } from '@utils/convertor';
import { ShowSuccess } from '@utils/notification';
import { addPrefixedRecordIdsToTemplates } from '@utils/prefixes-helper';
import { AUDIT_RECORD_ID_PREFIXES } from '@queries/audit';
import { auditRecordIdPrefixes } from '@queries/generated/audit.generated';
import { useGetRiskReviewsList } from '@apis/bff-api/report-templates';

export interface RiskReviewsGridProps {
    id: string;
    studyNumber: string;
    chipHeaders: GridHeaderTitle[];
    selectedChipHeader: number;
    onChipHeaderSelected: (chipHeader: number) => void;
    study?: NullableStudy;
}

const RiskReviewsGrid = ({
    id: studyId,
    studyNumber,
    chipHeaders,
    selectedChipHeader,
    onChipHeaderSelected,
    study,
}: RiskReviewsGridProps) => {
    const { t } = useTranslation();
    const { t: tCommon } = useTranslation('common');
    const user = useAuthorization();

    const canEdit = !!user?.canWrite?.({
        module: Constants.Modules.RAM,
        element: Constants.DataElements.RiskAssessment,
        permission: Permissions.ReadWrite,
    });

    const {
        data: riskReviewData,
        mutate: mutateRiskReviews,
        error: loadingRiskReviewsError,
        isValidating: isValidatingRiskReviews,
    } = useGetRiskReviewsList(studyId);

    const {
        data: auditPrefixesData,
        loading: isLoadingAuditPrefixes,
        error: loadingAuditPrefixesError,
    } = useQuery<auditRecordIdPrefixes>(AUDIT_RECORD_ID_PREFIXES, {
        fetchPolicy: 'cache-first',
    });

    const addActionRef = useRef<boolean>(false);

    const [currentRiskReview, setCurrentRiskReview] = useState<RiskReview | null>(null);

    const showManageRiskReviewDialog = useBoolean(false);
    const showDeleteRiskReviewDialog = useBoolean(false);
    const showOnlyOneOpenWarningDialog = useBoolean(false);
    const [hasOtherOpenRecord, setHasOtherOpenRecord] = useState(false);

    if ((loadingRiskReviewsError || loadingAuditPrefixesError) && !isValidatingRiskReviews) return <ErrorMessage />;

    const hasOpenRecord = (riskReviewData ?? []).some((r) => !r.endDate);

    const getGridData = () => {
        if (!riskReviewData || !auditPrefixesData) {
            return [];
        }

        const formedRiskReviewData = formRiskReviewData(riskReviewData);
        const prefixedRiskReviewData = addPrefixedRecordIdsToTemplates(
            formedRiskReviewData,
            AuditServiceType.REPORTING,
            auditPrefixesData.auditRecordIdPrefixes,
        );

        const ordered = orderBy(prefixedRiskReviewData, [
            { field: '_isOpen', dir: 'desc' },
            { field: 'startDate', dir: 'desc' },
        ]);

        return ordered.map((r) => ({
            ...r,
            status: r.endDate ? t('ram:riskLog.shared.closed') : t('ram:riskLog.shared.open'),
        }));
    };

    const onRiskReviewDialogSubmitted = async (riskReviewId: string | undefined) => {
        showManageRiskReviewDialog.setFalse();
        if (riskReviewId) {
            const riskReviews = await mutateRiskReviews();
            await globalMutate(`ReportTemplates/GetAttendees?studyId=${studyId}`);
            const selectedRiskReview = riskReviews?.find((riskReview) => riskReview.id === riskReviewId);
            setCurrentRiskReview(selectedRiskReview ?? null);
            addActionRef.current = true;
        }
    };

    const onDeleteRiskReviewClose = () => {
        showDeleteRiskReviewDialog.setFalse();
    };

    const onSubmitDeleteRiskReview = async () => {
        await mutateRiskReviews();
        ShowSuccess(tCommon('actionsSuccessfulToast'));
        showDeleteRiskReviewDialog.setFalse();
        setCurrentRiskReview(null);
    };

    return (
        <div className="k-h-full">
            {showManageRiskReviewDialog.value && (
                <ManageRiskReviewDialog
                    studyId={studyId}
                    riskReview={currentRiskReview || undefined}
                    hasOtherOpenRecord={hasOtherOpenRecord}
                    onSubmit={(riskReviewId) => onRiskReviewDialogSubmitted(riskReviewId)}
                    onClose={() => showManageRiskReviewDialog.setFalse()}
                    study={study}
                />
            )}
            {showOnlyOneOpenWarningDialog.value && (
                <DraggableDialog
                    title={t('ra:addRiskReview')}
                    dataTestId="add-risk-review-only-one-open-dialog"
                    initialWidth={500}
                    initialHeight={180}
                    onClose={() => showOnlyOneOpenWarningDialog.setFalse()}
                    secondaryBtnLabel={t('common:close')}
                >
                    <p>{t('ram:onlyOneOpenRiskReviewWarning')}</p>
                </DraggableDialog>
            )}
            {showDeleteRiskReviewDialog.value && !!currentRiskReview && (
                <DeleteRiskReviewDialog
                    onClose={onDeleteRiskReviewClose}
                    onSubmit={onSubmitDeleteRiskReview}
                    riskReview={currentRiskReview}
                />
            )}
            <Grid
                excludeHiddenRowMenuActions
                title={chipHeaders}
                onChipHeaderSelected={onChipHeaderSelected}
                selectedChipHeader={selectedChipHeader}
                selectedTitle={chipHeaders.find((header) => header.id === selectedChipHeader)}
                maxHeight="auto"
                testDataId="risk-reviews-grid"
                data={getGridData()}
                columns={getRiskReviewsColumns(t)}
                loading={isValidatingRiskReviews || isLoadingAuditPrefixes}
                useLoadingOverlay
                showAddIcon
                canEdit={canEdit}
                clearGridSelectedRows={!currentRiskReview}
                defaultSelectedItem={addActionRef.current ? (currentRiskReview ?? undefined) : undefined}
                addRecordTooltip={t('ra:addRiskReview')}
                addCallback={() => {
                    if (hasOpenRecord) {
                        showOnlyOneOpenWarningDialog.setTrue();
                    } else {
                        setCurrentRiskReview(null);
                        showManageRiskReviewDialog.setTrue();
                    }
                }}
                scrollable="virtual"
                dataItemKey="id"
                rowMenuActions={[
                    {
                        id: 1,
                        label: t('ra:editRiskReview'),
                        onClick: (dataItem) => {
                            const review = dataItem as RiskReview;
                            setCurrentRiskReview(review);
                            setHasOtherOpenRecord((riskReviewData ?? []).some((r) => !r.endDate && r.id !== review.id));
                            addActionRef.current = false;
                            showManageRiskReviewDialog.setTrue();
                        },
                        disabled: !canEdit,
                        icon: ['far', 'pen-to-square'],
                    },
                    {
                        id: 2,
                        label: t('ra:deleteRiskReview'),
                        onClick: (dataItem) => {
                            setCurrentRiskReview(dataItem as RiskReview);
                            showDeleteRiskReviewDialog.setTrue();
                        },
                        icon: ['fas', 'trash-xmark'],
                        colorProp: 'Red',
                        disabled: !canEdit,
                    },
                ]}
                exportFilename={`Risk Reviews_${studyNumber}_${stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-YYYY')}`}
                service={AuditServiceType.CORE}
                area={AuditApplicationArea.RISK_ASSESSMENT}
                dataType={AuditDataType.EXCEL_EXPORT}
                persistentStorageKey={String(PersistentStorageKeys['manage_risk-reviews-risks'])}
            />
        </div>
    );
};

export default RiskReviewsGrid;
